import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Drawer, Space, Button, Input } from 'antd';
import { Template } from '../../../../interfaces/backend/Template';
import { TemplatesPOSTRequest } from '../../../../interfaces/backend/TemplatesPOSTRequest';
import TemplateCardContextMenu from '../TemplateCardContextMenu';
import { liquidizeTemplate } from '../../LiquidizedPreview';
import { format } from 'date-fns';
import { SMSPreview } from './SMSPreview';
import { blue } from '@ant-design/colors';
import styled from 'styled-components';

const FormItem = Form.Item;
type SMSTemplate = Extract<Template, { channel: 'SMS' | 'CALL' }>; // without the | 'CALL', Extract does not work correctly

interface Props {
  template: SMSTemplate;
  deleteTemplate: (channel: string, templateId: string) => Promise<void>;
  makeDefault: (channel: string, templateId: string) => Promise<void>;
  patchTemplate: (
    channel: string,
    templateId: string,
    params: Record<string, unknown>
  ) => Promise<void>;
  createTemplate: (
    channel: string,
    params: TemplatesPOSTRequest
  ) => Promise<void>;
  setTemplateBeingDuplicated: (template: Template) => void;
}

const CustomCard = styled(Card)`
  cursor: pointer;
  &:hover {
    border: 1px solid ${blue.primary};
    transition: border 0.3s ease;
  }
`;

const SmsTemplateCard: React.FC<Props> = (props: Props) => {
  const [openedTemplateEditor, setOpenedTemplateEditor] =
    useState<SMSTemplate>();
  const [form] = Form.useForm();
  const [saveTemplate, changeSaveTemplate] = useState(false);
  const [liquidizedTemplate, setLiquidizedTemplate] = useState<SMSTemplate>();
  const [previewError, setPreviewError] = useState<React.ReactNode>();
  const [previewWarning, setPreviewWarning] = useState<React.ReactNode>();

  const onFinish = async (value: { text: string }) => {
    changeSaveTemplate(true);
    await props.patchTemplate(
      props.template.channel,
      props.template.templateId,
      {
        text: value.text
      }
    );
    changeSaveTemplate(false);
    setOpenedTemplateEditor(undefined);
  };

  const generateActionButtons = (): JSX.Element[] => {
    const buttons = [
      <TemplateCardContextMenu
        template={props.template}
        setTemplateBeingDuplicated={props.setTemplateBeingDuplicated}
        makeDefault={props.makeDefault}
        deleteTemplate={props.deleteTemplate}
      />
    ];

    return buttons;
  };
  const time = format(new Date(), 'h:mm'); // Time format: 9:34
  useEffect(() => {
    const { result, warning, error } = liquidizeTemplate(props.template);
    setLiquidizedTemplate(result as SMSTemplate);
    setPreviewWarning(warning);
    setPreviewError(error);
  }, [props.template]);

  return (
    <Col span={24} lg={12} xl={8}>
      <CustomCard
        onClick={() => {
          setOpenedTemplateEditor(liquidizedTemplate);
        }}
        title={'SMS'}
        cover={
          <Row justify="center">
            {!previewError && (
              <SMSPreview text={liquidizedTemplate?.text} time={time} />
            )}
          </Row>
        }
        actions={generateActionButtons()}
      ></CustomCard>
      <Drawer
        destroyOnClose
        open={openedTemplateEditor ? true : false}
        onClose={() => setOpenedTemplateEditor(undefined)}
        title="SMS Template Editor"
        width={500}
        styles={{
          body: {
            paddingBottom: 80
          }
        }}
        extra={
          <Space>
            <Button
              loading={saveTemplate}
              onClick={() => {
                form.submit();
              }}
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
            <Button
              onClick={(): void => {
                setOpenedTemplateEditor(undefined);
              }}
            >
              Cancel
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          onFinish={(value) => {
            onFinish(value);
          }}
          layout="vertical"
        >
          <FormItem
            label="Text:"
            name="text"
            initialValue={props.template.text}
          >
            <Input.TextArea placeholder={props.template.text} autoSize />
          </FormItem>
        </Form>
      </Drawer>
      {previewWarning}
      {previewError}
    </Col>
  );
};

export default SmsTemplateCard;
